import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

export default function QualiteBoisForm() {
  const { t, ui } = useI18n();

  const location = useLocation();

  const [operator, setOperator] = useState("")

  const today = new Date().toISOString().split("T")[0];

  /* ============================================================
     🔵 DATA MODELS
  ============================================================ */
  const initialTechnicalSamples = [
    { sample: "", grade: "", humidite: "", moisissure: "", autre: "" },
    { sample: "", grade: "", humidite: "", moisissure: "", autre: "" },
    { sample: "", grade: "", humidite: "", moisissure: "", autre: "" },
    { sample: "", grade: "", humidite: "", moisissure: "", autre: "" },
  ];

  const initialCommentSamples = [
    { sample: "", commentaire: "" },
    { sample: "", commentaire: "" },
    { sample: "", commentaire: "" },
    { sample: "", commentaire: "" },
  ];

  const initialFormData = {
    Date: today,
    operator,
    LotFournisseur: "",
    Fournisseur: "",
    Palette: "",
    QuantiteBois: "",
    QuantiteNOK: "",
    Samples: initialTechnicalSamples,       // toujours visible
    SamplesComments: initialCommentSamples, // NOK only
  };

  const [formData, setFormData] = useState(initialFormData);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [messageColor, setMessageColor] = useState("#fff");
  const [submitting, setSubmitting] = useState(false);

  /* ============================================================
     🔵 FOURNISSEURS (comme BoisForm)
  ============================================================ */
  const [options, setOptions] = useState({ fournisseurs: [] });
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const canon = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const getCodeByName = (list, name) => {
    if (!name) return "";
    const raw = String(name).trim();

    const exact = list.find(
      (x) => String(x.nom || "").trim().toLowerCase() === raw.toLowerCase()
    );
    if (exact) return exact.code;

    const ctarget = canon(raw);
    let hit = list.find((x) => canon(x.nom) === ctarget);
    if (hit) return hit.code;

    hit = list.find(
      (x) => canon(x.nom).includes(ctarget) || ctarget.includes(canon(x.nom))
    );
    return hit ? hit.code : "";
  };


  useEffect(() => {
        const raw = localStorage.getItem("user");
        if (raw) {
            try {
                const u = JSON.parse(raw);
                setOperator(u.username || "");
            }catch (e) {
                console.error(e);
            }
        }
    }, []);




  /* ============================================================
     🔵 LOAD FOURNISSEURS
  ============================================================ */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/api/options");
        if (!res.ok) throw new Error("Erreur serveur");
        const data = await res.json();

        if (!cancelled) {
          setOptions({
            fournisseurs: data.fournisseurs || [],
          });
          setOptionsLoaded(true);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setMessage("Impossible de charger les fournisseurs.");
          setMessageColor("#ffb3b3");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ============================================================
     🔵 EDIT MODE LOAD EXISTING RECORD
  ============================================================ */
  useEffect(() => {
    if (!optionsLoaded) return;

    const fromState = location.state?.item;
    const fromStorage =
      !fromState
        ? JSON.parse(localStorage.getItem("woodoo_edit_item") || "null")
        : null;

    const item = fromState || fromStorage;

    if (item) {
      item.Fournisseur = getCodeByName(options.fournisseurs, item.Fournisseur);

      // parse JSON samples stored as string in DynamoDB
      try {
        item.Samples = JSON.parse(item.Samples || "[]");
      } catch {
        item.Samples = initialTechnicalSamples;
      }

      try {
        item.SamplesComments = JSON.parse(item.SamplesComments || "[]");
      } catch {
        item.SamplesComments = initialCommentSamples;
      }

      setFormData((prev) => ({ ...prev, ...item }));
      setEditing(true);
      localStorage.removeItem("woodoo_edit_item");
    }
  }, [optionsLoaded, location.state]);

  /* ============================================================
     🔵 INPUT HANDLERS
  ============================================================ */
const handleChange = (e) => {
  let { name, value, type } = e.target;

  if (type === "number" && Number(value) < 0) {
    value = "";
  }

  setFormData((s) => ({ ...s, [name]: value }));
};


  const updateSample = (index, field, value) => {
    const updated = [...formData.Samples];
    updated[index][field] = value;
    setFormData((s) => ({ ...s, Samples: updated }));
  };

  const updateCommentSample = (index, field, value) => {
    const updated = [...formData.SamplesComments];
    updated[index][field] = value;
    setFormData((s) => ({ ...s, SamplesComments: updated }));
  };

  /* ============================================================
     🔥 SUBMIT
  ============================================================ */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");

    try {
      const url = editing
        ? "/api/qualite/mpbois/update"
        : "/api/qualite/mpbois/add";

      const r = await apiFetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await r.json();

      setMessage(result.message || "Terminé");
      setMessageColor(result.status === "success" ? "#2ecc71" : "#e74c3c");

      if (result.status === "success" && !editing) {
        setFormData(initialFormData);
      }
    } catch (e) {
      setMessage("Erreur réseau");
      setMessageColor("#e74c3c");
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================================================
     🔵 SHOW NOK TABLE IF QuantiteNOK > 0
  ============================================================ */
  const showCommentsTable = Number(formData.QuantiteNOK) > 0;

  /* ============================================================
     🎨 UI RENDER
  ============================================================ */
  return (
    <div style={styles.page}>
      <MenuBar active="qualite" />

      <div style={styles.wrap}>
        <section style={styles.panel}>
          <div style={styles.titleTop}>
            CONFORMITÉ MATIÈRE PREMIÈRE – BOIS
          </div>

          {editing && (
            <div style={styles.editBanner}>
              ⚠️ Vous modifiez un enregistrement existant
            </div>
          )}

          <div style={styles.card}>
            <div style={styles.sectionHeader}>FICHE DE CONTRÔLE</div>

            <form onSubmit={handleSubmit} style={styles.form}>

              {renderRow("DATE", (
                <input
                  type="date"
                  name="Date"
                  value={formData.Date}
                  onChange={handleChange}
                  style={styles.input}
                />
              ))}

              {renderRow(
  "OPERATEUR",
  <input
    type="text"
    name="operator"
    onChange={handleChange}
    value={operator ? operator.toUpperCase() : "OPÉRATEUR NON CONNECTÉ"}
    readOnly
    style={styles.input}
  />
)}
              {renderRow("FOURNISSEUR", (
                <select
                  name="Fournisseur"
                  value={formData.Fournisseur}
                  onChange={handleChange}
                  style={styles.input}
                >
                    {operator ? operator.toUpperCase() : "OPÉRATEUR NON CONNECTÉ"}
                  <option value=""></option>
                  {options.fournisseurs.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.nom}
                    </option>
                  ))}
                </select>
              ))}

              {renderRow("LOT FOURNISSEUR", (
                <input
                  name="LotFournisseur"
                  value={formData.LotFournisseur}
                  onChange={handleChange}
                  style={styles.input}
                />
              ))}

              {renderRow("PALETTE", (
                <input
                  name="Palette"
                  value={formData.Palette}
                  onChange={handleChange}
                  style={styles.input}
                />
              ))}

              {renderRow("BOIS (kg)", (
                <input
                  type="number"
                  name="QuantiteBois"
                  value={formData.QuantiteBois}
                  onChange={handleChange}
                  style={styles.input}
                />
              ))}
              {renderRow("KG NOK", (
                <input
                  type="number"
                  name="QuantiteNOK"
                  value={formData.QuantiteNOK}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    background:
                      Number(formData.QuantiteNOK) > 0 ? "#ffdddd" : "#fff",
                  }}
                />
              ))}

              {/* TABLE TECHNIQUE */}
              <div style={styles.samplesTitle}>MESURES TECHNIQUES</div>

              <table style={styles.table}>
                <thead>
                  <tr>
                    {["Sample","Grade","Humidité","Moisissure","Autre"]
                      .map((h) => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                  </tr>
                </thead>

                <tbody>
                  {formData.Samples.map((row, i) => (
                    <tr key={i}>
                      {Object.keys(row).map((field) => (
                        <td key={field}>
                          <input
                            style={styles.tdInput}
                            value={row[field]}
                            onChange={(e) =>
                              updateSample(i, field, e.target.value)
                            }
                            type={field === "visuel" ? "text" : "number"}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* TABLE COMMENTAIRE SI NOK */}
              {showCommentsTable && (
                <div style={{ marginTop: 25 }}>
                  <div style={styles.simpleSamplesTitle}>
                    SAMPLES / COMMENTAIRES (obligatoire si NOK)
                  </div>

                  <table style={styles.simpleTable}>
                    <thead>
                      <tr>
                        <th style={styles.simpleTh}>Sample</th>
                        <th style={styles.simpleTh}>Commentaire</th>
                      </tr>
                    </thead>

                    <tbody>
                      {formData.SamplesComments.map((row, i) => (
                        <tr key={i}>
                          <td>
                            <input
                              style={styles.simpleTdInput}
                              value={row.sample}
                              onChange={(e) =>
                                updateCommentSample(i, "sample", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              style={styles.simpleTdInput}
                              value={row.commentaire}
                              onChange={(e) =>
                                updateCommentSample(
                                  i,
                                  "commentaire",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={styles.saveBtn}
              >
                {submitting
                  ? "Enregistrement..."
                  : editing
                  ? "METTRE À JOUR"
                  : "SAUVEGARDER"}
              </button>

              {message && (
                <div style={{ ...styles.message, color: messageColor }}>
                  {message}
                </div>
              )}
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ============================================================
   🔧 ROW RENDERER
============================================================ */
function renderRow(label, field) {
  return (
    <div style={styles.row}>
      <div style={styles.rowLabel}>{label}</div>
      <div style={styles.rowField}>{field}</div>
    </div>
  );
}

/* ============================================================
   🎨 STYLES
============================================================ */
const styles = {
  page: { minHeight: "100vh", background: "#000", color: "#fff" },
  wrap: { padding: "0 20px 40px" },
  panel: {
    background: "#4c525a",
    padding: 20,
    borderRadius: 8,
    marginTop: 20,
    border: "1px solid #333",
  },
  titleTop: {
    color: "white",
    fontWeight: 900,
    fontSize: 18,
    marginBottom: 10,
  },
  editBanner: {
    background: "#ffca28",
    color: "#000",
    padding: 10,
    textAlign: "center",
    borderRadius: 4,
    fontWeight: 700,
    marginBottom: 12,
  },
  card: {
    background: "#f4f5f7",
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #ccc",
  },
  sectionHeader: {
    background: "#d5d7da",
    padding: "12px 16px",
    fontWeight: 900,
    color: "#000",
    borderBottom: "1px solid #c9cbd1",
  },

  /* Form styles */
  form: { padding: 16, background: "#fff" },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    marginBottom: 12,
    alignItems: "center",
  },
  rowLabel: {
    fontWeight: 800,
    fontSize: 14,
    color: "#444",
  },
  rowField: {},
  input: {
    width: "100%",
    height: 38,
    padding: "0 8px",
    borderRadius: 4,
    border: "1px solid #b8b8b8",
    fontSize: 15,
  },

  /* Table technique */
  samplesTitle: {
    marginTop: 25,
    marginBottom: 8,
    fontWeight: 800,
    fontSize: 15,
    color: "#000",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: 25,
  },
  th: {
    background: "#ff914d",
    padding: 10,
    fontSize: 14,
    color: "#000",
    fontWeight: 800,
    border: "1px solid #d38b4d",
    textAlign: "center",
  },
  tdInput: {
    width: "100%",
    height: 34,
    border: "1px solid #ccc",
    padding: 4,
    fontSize: 14,
    borderRadius: 4,
  },

  /* Table Sample/Commentaire */
  simpleSamplesTitle: {
    fontWeight: 800,
    fontSize: 15,
    color: "#000",
    marginBottom: 8,
  },
  simpleTable: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: 25,
  },
  simpleTh: {
    background: "#ff914d",
    padding: 10,
    fontSize: 14,
    color: "#000",
    fontWeight: 800,
    border: "1px solid #d38b4d",
    textAlign: "center",
  },
  simpleTdInput: {
    width: "100%",
    height: 34,
    padding: "4px 6px",
    borderRadius: 4,
    border: "1px solid #ccc",
    background: "#ffecec",
    fontSize: 14,
  },

  /* Submit / message */
  saveBtn: {
    width: "100%",
    background: "#E6FF3E",
    border: "none",
    padding: "14px 0",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
    borderRadius: 6,
    marginTop: 10,
    color: "#000",
  },
  message: {
    marginTop: 12,
    fontWeight: 800,
    textAlign: "center",
    fontSize: 15,
  },
};
